package com.oasisfeng.invaluable.p2p.core

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjectionScheme
import com.oasisfeng.invaluable.event.CapabilityWrap
import com.oasisfeng.invaluable.identity.Codec
import kotlinx.io.bytestring.ByteString

/**
 * A directed disclosure envelope (Milestone 13 / roadmap §20.3, KTD4): a [CapabilityWrap] addressed to one recipient,
 * carried over the **transport layer only** — never a [com.oasisfeng.invaluable.event.ReplicatedEvent], never stored,
 * never custodied.
 *
 * "Disclosure = forwarding": a key-holder emits one of these toward a recipient (riding the same gossip mesh as the
 * `recommend`/`relay` route delivery). Because the mesh has no routing table, delivery is **best-effort flood** — a
 * relay forwards it onward **without reading** (the wrap is boxed to the recipient's identity, so only the addressee
 * can open it), and it loop-prevents by a bounded seen-set rather than by store dedup (it is not stored). A dropped
 * envelope is harmless: any holder can re-wrap the content-key in microseconds, so the envelope is regenerated, never
 * recovered from storage.
 *
 * [recipientPeerId] is the addressee's `base64url(SPKI)` ([com.oasisfeng.invaluable.identity.Identity.peerId]). It is a
 * **delivery optimization hint, not an authorization field** (review P3): authority over "is this for me" rests
 * entirely on [CapabilityWrap.open], which succeeds only for the addressee whose private key the box was sealed to. A
 * node MAY use the hint to skip the unwrap attempt for an envelope plainly not its own (worthwhile under flood, where
 * every node sees every envelope); the cost of a forged or stale hint is therefore a *missed delivery* — exactly a
 * dropped packet, which any on-path attacker can already cause on this best-effort mesh, and which any holder repairs
 * by re-wrapping. So the hint is no new attack surface, and it is deliberately not bound into the box.
 *
 * The wrap's [CapabilityWrap.contentProjection] payload rides this carrier as a scheme-free field (the scheme is
 * re-attached on [decode] from the local store's single scheme, per the [com.oasisfeng.invaluable.event.LineWireCodec]
 * convention); its integrity is the box's, not the transport's (review P1).
 *
 * @property recipientPeerId The addressee's peerId — `base64url(SPKI)`; a routing hint only (see above).
 * @property wrap The content-key boxed to that recipient, authenticated to its author and bound to its content.
 */
data class DirectedEnvelope(val recipientPeerId: String, val wrap: CapabilityWrap) {

    /** Encode to one transport line; all binary fields are base64url, the projection scheme-free per the wire convention. */
    fun encode(): String = listOf(
        TAG,
        recipientPeerId,
        Codec.base64Url(wrap.author.toByteArray()),
        Codec.base64Url(wrap.nonce.toByteArray()),
        Codec.base64Url(wrap.wrap.toByteArray()),
        Codec.base64Url(wrap.contentProjection.payload.toByteArray()),
    ).joinToString(SEPARATOR)

    companion object {
        const val TAG = "ENV"
        private const val SEPARATOR = "|"

        /**
         * Decode a transport line back to an envelope, re-attaching [scheme] to the carried projection payload, or null
         * if it is not a well-formed [TAG] line.
         */
        fun decode(line: String, scheme: LinkProjectionScheme): DirectedEnvelope? {
            val parts = line.trim().split(SEPARATOR)
            if (parts.size < 6 || parts[0] != TAG) return null
            return runCatching {
                DirectedEnvelope(
                    recipientPeerId = parts[1],
                    wrap = CapabilityWrap(
                        author = ByteString(*Codec.base64UrlDecode(parts[2])),
                        nonce = ByteString(*Codec.base64UrlDecode(parts[3])),
                        wrap = ByteString(*Codec.base64UrlDecode(parts[4])),
                        contentProjection = LinkProjection(scheme, ByteString(*Codec.base64UrlDecode(parts[5]))),
                    ),
                )
            }.getOrNull()
        }
    }
}
